import httpClient from '~/lib/http-client';

// Owner-facing Email marketing plugin API (studio `EmailMarketingOwnerController`).
// `appId` is the CHAT id. Secret credentials are write-only: the view only
// says which ones are stored (`secretsSet`).

export type EmailProvider = 'klaviyo' | 'mailchimp' | 'brevo';

export interface EmailTriggers {
  forms: boolean;
  funnels: boolean;
  customers: boolean;
  accounts: boolean;
}

export type EmailTrigger = keyof EmailTriggers;

export type EmailSyncStatus = 'syncing' | 'synced' | 'failed' | 'skipped_no_consent' | 'skipped_test_mode';

export interface EmailSyncLogRow {
  provider: string;
  sourceRef: string;
  email: string;
  status: EmailSyncStatus;
  lastError: string | null;
  createdAt: string | null;
}

export interface EmailMarketingView {
  provider: EmailProvider | null;
  configured: boolean;
  enabled: boolean;
  /** Non-secret settings (listId / audienceId). */
  config: Record<string, string>;
  secretsSet: string[];
  triggers: EmailTriggers;
  lastSyncs: EmailSyncLogRow[];
}

export interface EmailMarketingInput {
  provider: EmailProvider;
  /** Secret and non-secret provider fields. An empty secret keeps the stored one. */
  credentials?: Record<string, string>;
  triggers?: Partial<EmailTriggers>;
  enabled?: boolean;
}

const base = (appId: string) => `/api/plugins/email-marketing/apps/${encodeURIComponent(appId)}`;

export async function apiGetEmailMarketing(appId: string): Promise<EmailMarketingView> {
  const { data } = await httpClient.get<EmailMarketingView>(base(appId));
  return data;
}

export async function apiSaveEmailMarketing(appId: string, input: EmailMarketingInput): Promise<EmailMarketingView> {
  const { data } = await httpClient.put<EmailMarketingView>(base(appId), input);
  return data;
}

export async function apiTestEmailMarketing(appId: string): Promise<{ ok: boolean; message: string }> {
  const { data } = await httpClient.post<{ ok: boolean; message: string }>(`${base(appId)}/test`, {});
  return data;
}
